import React, { useMemo, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
  Divider,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FiMail, FiPhone, FiMessageCircle, FiMapPin } from "react-icons/fi";
import CollaborationsPage from "./Collaborations";

const services = [
  "Website Development",
  "UI/UX Design",
  "Landing Page",
  "E-commerce",
  "Bug Fix / Support",
  "SEO",
  "Other",
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "rgba(0,0,0,.16)" },
    "&:hover fieldset": { borderColor: "rgba(0,0,0,.28)" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
};

const CardLink = ({ title, value, href, icon }) => (
  <Box
    component="a"
    href={href}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel={href.startsWith("http") ? "noreferrer" : undefined}
    sx={{
      textDecoration: "none",
      p: 1.4,
      borderRadius: 2,
      border: "1px solid rgba(0,0,0,.10)",
      display: "flex",
      gap: 1.2,
      alignItems: "center",
      color: "inherit",
      background: "#fff",
      transition: ".18s ease",
      "&:hover": { transform: "translateY(-1px)", borderColor: "rgba(0,0,0,.22)" },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        display: "grid",
        placeItems: "center",
        background: "rgba(25,118,210,.10)",
        border: "1px solid rgba(0,0,0,.08)",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, lineHeight: 1.1 }}>{title}</Typography>
      <Typography
        sx={{
          color: "rgba(0,0,0,.70)",
          fontSize: 13.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

export default function Contact() {
  const RECEIVER_EMAIL = "sasinnovates@gmail.com"; // display only

  // EmailJS env (CRA)
  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  console.log("ENV(Contact):", SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY);

  const [form, setForm] = useState({
    name: "",
    email: "",
    service: "",
    subject: "",
    message: "",
  });

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const [sending, setSending] = useState(false);

  const contactCards = useMemo(
    () => [
      {
        title: "Email",
        value: RECEIVER_EMAIL,
        icon: <FiMail size={20} />,
        href: `mailto:${RECEIVER_EMAIL}`,
      },
      {
        title: "Phone",
        value: "+91 8802123273",
        icon: <FiPhone size={20} />,
        href: "tel:+91 8802123273",
      },
      {
        title: "WhatsApp",
        value: "Chat on WhatsApp",
        icon: <FiMessageCircle size={20} />,
        href: "https://wa.me/918802123273?text=Hi%20I%20want%20to%20contact%20you",
      },
      {
        title: "31C Gulistan Mart,Emerald Gulistan,Jajmau",
        value: "Kanpur  Uttar Pradesh, India",
        icon: <FiMapPin size={20} />,
        href: "https://maps.google.com",
      },
    ],
    [RECEIVER_EMAIL]
  );

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (!form.email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return "Please enter a valid email";
    if (!form.service) return "Please choose a service";
    if (!form.message.trim()) return "Message is required";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) return setToast({ open: true, type: "error", msg: err });

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      return setToast({
        open: true,
        type: "error",
        msg: "EmailJS keys missing. Please check .env and restart server.",
      });
    }

    setSending(true);

    try {
      // Must match EmailJS template variables
      const templateParams = {
        name: form.name,
        email: form.email,
        service: form.service,
        subject: form.subject || "-",
        message: form.message,
        submittedAt: new Date().toLocaleString(),
      };

      const res = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );

      console.log("EmailJS(Contact) response:", res);

      setToast({
        open: true,
        type: "success",
        msg: "✅ Thanks! Your message has been sent.",
      });

      setForm({ name: "", email: "", service: "", subject: "", message: "" });
    } catch (error) {
      console.error("EmailJS(Contact) ERROR full:", error);

      setToast({
        open: true,
        type: "error",
        msg: `❌ Failed to send: ${error?.text || error?.message || "Unknown error"}`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
     <CollaborationsPage />
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 6 }, background: "#fff" }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Contact Me
          </Typography>
          <Typography sx={{ color: "rgba(0,0,0,.70)", maxWidth: 720 }}>
            If you want to contact me, use the form below. Share your requirement
            and I’ll get back to you soon.
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          {/* Left: Form */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,.10)",
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(0,0,0,.08)" }}>
                <Typography sx={{ fontWeight: 900 }}>How can I help you?</Typography>
                <Typography sx={{ color: "rgba(0,0,0,.65)", mt: 0.5, fontSize: 14 }}>
                  Tell me what you need — I’ll suggest the best solution.
                </Typography>
              </Box>

              <Box component="form" onSubmit={onSubmit} sx={{ p: 2.5 }}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    sx={fieldSx}
                  />

                  <TextField
                    fullWidth
                    label="Your Email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    sx={fieldSx}
                  />

                  <TextField
                    fullWidth
                    select
                    label="How can I help you with?"
                    name="service"
                    value={form.service}
                    onChange={onChange}
                    sx={fieldSx}
                  >
                    {services.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    label="Subject (optional)"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    sx={fieldSx}
                  />

                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Your Message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    sx={fieldSx}
                  />

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={sending}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 900,
                        px: 2.5,
                      }}
                    >
                      {sending ? "Sending..." : "Send Message"}
                    </Button>

                    <Typography sx={{ color: "rgba(0,0,0,.60)", fontSize: 13 }}>
                      By submitting, you agree to be contacted back.
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* Right: Contact Info */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,.10)",
                background: "#fff",
                overflow: "hidden",
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>Quick Contact</Typography>
                <Typography sx={{ color: "rgba(0,0,0,.70)", fontSize: 14 }}>
                  You can also reach me directly:
                </Typography>

                <Divider sx={{ my: 2, borderColor: "rgba(0,0,0,.10)" }} />

                <Stack spacing={1.2}>
                  {contactCards.map((c) => (
                    <CardLink key={c.title} {...c} />
                  ))}
                </Stack>

                
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Toast */}
        <Snackbar
          open={toast.open}
          autoHideDuration={3500}
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={toast.type}
            variant="filled"
            sx={{ alignItems: "center" }}
            action={
              <IconButton
                size="small"
                onClick={() => setToast((p) => ({ ...p, open: false }))}
                sx={{ color: "white" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {toast.msg}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
    </>
  );
}
